import React, { useState, useRef } from 'react';
import { Bitcoin, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import FileUploadSection from './FileUploadSection';
import { inputClass, labelClass } from './vaultUtils';

const CryptoEntryModal = ({ mode, entry, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    save_as: entry?.save_as || '',
    wallet_address: entry?.data?.wallet_address || '',
    platform: entry?.data?.platform || '',
    key_information: entry?.data?.key_information || ''
  });

  const [files, setFiles] = useState(entry?.files || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.save_as.trim()) {
      toast.error('Please enter a name to save as');
      return;
    }
    onSave(formData, pendingFiles);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="crypto-entry-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <Bitcoin className="w-5 h-5 text-orange-400" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Add Crypto Asset' : 'Edit Crypto Asset'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          <FileUploadSection 
            mode={mode} 
            entry={entry} 
            files={files} 
            setFiles={setFiles} 
            pendingFiles={pendingFiles} 
            setPendingFiles={setPendingFiles} 
            uploading={uploading} 
            setUploading={setUploading} 
            fileInputRef={fileInputRef} 
          />

          <div className="space-y-2">
            <Label className={labelClass}>Save As *</Label>
            <Input name="save_as" value={formData.save_as} onChange={handleChange} placeholder="e.g., Bitcoin Wallet" className={inputClass} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Platform</Label>
              <Input name="platform" value={formData.platform} onChange={handleChange} placeholder="e.g., Coinbase, Binance" className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Wallet Address</Label>
              <Input name="wallet_address" value={formData.wallet_address} onChange={handleChange} placeholder="Wallet address" className={inputClass} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Key Information</Label>
            <textarea
              name="key_information"
              value={formData.key_information}
              onChange={handleChange}
              rows="3"
              placeholder="Important recovery phrases, seed words, or access instructions (keep secure!)"
              className={`${inputClass} w-full rounded-md p-2`}
            ></textarea>
            <p className="text-xs text-amber-400">⚠️ Store sensitive information securely</p>
          </div>
        </form>

        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button type="submit" onClick={handleSubmit} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
            {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving...</> : mode === 'add' ? 'Add Entry' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CryptoEntryModal;
